import { ShippingLocationGroupsDocument, useShippingLocationGroupsQuery } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import Select from '@/gf/components/next/Select'
import Spinner from '@/gf/components/Spinner'

export const Queries = [ShippingLocationGroupsDocument]

type LocationGroupOption = {
  value: string
  label: string
}

type LocationGroupSelectorProps = {
  value: string[]
  onChange: (value: string[]) => void
  label?: string
  placeholder?: string
  isLoading?: boolean
}

const LocationGroupSelector = ({
  value,
  onChange,
  label,
  placeholder = 'Select location groups...',
  isLoading = false,
}: LocationGroupSelectorProps) => {
  const { orgId } = useSession()
  const client = useGqlClient()

  const { data, loading, error } = useShippingLocationGroupsQuery({
    client,
    variables: { orgId, filter: null },
    skip: !orgId,
  })

  const locationGroups = data?.org?.shippingLocationGroups ?? []

  const options: LocationGroupOption[] = locationGroups.map((group) => ({
    value: group.id,
    label: group.name,
  }))

  const selectedOptions = options.filter((option) => value.includes(option.value))

  const handleChange = (newValue: readonly LocationGroupOption[]) => {
    onChange(newValue.map((option) => option.value))
  }

  if (loading) {
    return <div className="text-sm text-gray-500">Loading location groups...</div>
  }

  if (error) {
    return (
      <div className="text-sm text-red-600">Failed to load location groups. Please try again.</div>
    )
  }

  if (locationGroups.length === 0) {
    return null
  }

  return (
    <div className="space-y-2">
      {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
      <div className="relative">
        <Select
          isMulti
          isDisabled={isLoading}
          options={options}
          value={selectedOptions}
          onChange={(newValue) => handleChange(newValue as readonly LocationGroupOption[])}
          placeholder={placeholder}
          isClearable
        />
        {isLoading && (
          <div className="absolute right-10 top-1/2 -translate-y-1/2 pointer-events-none">
            <Spinner size="xs" color="blue" className="mx-0" />
          </div>
        )}
      </div>
    </div>
  )
}

export default LocationGroupSelector
